import {
  Box,
  FlexBox,
  ModalBody,
  ModalFoot,
  ModalHead,
} from '@dixa/lib-shared-habla';
import { boundMethod } from 'autobind-decorator';
import { Component } from 'react';
import { v4 as uuid } from 'uuid';

import { getConfirmButtonVerb } from 'components/powerFlowShared/helpers/getConfirmButtonVerb';
import { FieldDuration } from 'fable/fieldDuration';
import {
  ActionNodeT,
  FlowManagerEditModalComponentT,
  FlowType,
} from 'fable/flowEditor';
import { AutomationFlowActionT, AutomationNodeTypes } from 'hocs';
import { durationConverter } from 'utils/duration';
import { trackFlowActionSaved } from 'utils/flowEditor/tracking';

import type {
  AutomationNodeWaitPayloadT,
  AutomationNodeWaitT,
} from '../nodeTypes';

import styles from './shared.module.scss';

type PropsT = FlowManagerEditModalComponentT<AutomationNodeWaitT>;
type StateT = {
  payload: AutomationNodeWaitPayloadT;
};

export class WaitModal extends Component<PropsT, StateT> {
  constructor(props: PropsT) {
    super(props);
    const { node } = props;

    this.state = {
      payload:
        node.nodeType === AutomationNodeTypes.WAIT
          ? node.payload
          : {
              delayInSeconds: 3600,
              matchNodeId: uuid(),
              nonMatchNodeId: uuid(),
            },
    };
  }

  handleAnalytics(): void {
    const { node } = this.props;
    const { payload } = this.state;

    trackFlowActionSaved(!!node.isAction, {
      flowType: FlowType.FLOW_TYPE_AUTOMATION,
      nodeType: AutomationNodeTypes.WAIT,
      settings: {
        delayInMinutes: payload.delayInSeconds / 60,
      },
    });
  }

  @boundMethod
  saveHandler(): void {
    setTimeout(() => {
      // Fixes blur triggering after submit in Firefox
      this.saveNode();
    }, 1);
  }

  @boundMethod
  saveNode(): void {
    const { payload } = this.state;

    this.handleAnalytics();

    const nodeOptions: AutomationNodeWaitT = {
      ...this.props.node,
      nodeType: AutomationNodeTypes.WAIT,
      isAction: false,
      isRemovable: true,
      isEditable: true,
      payload: {
        ...payload,
        delayInSeconds: payload.delayInSeconds || 60,
      },
    };

    this.props.replaceNode(nodeOptions);
    this.closeModal();
  }

  @boundMethod
  closeModal(): void {
    this.props.triggerClose();
  }

  getActionWaitTimeRecursive(
    action: AutomationFlowActionT | ActionNodeT,
    includeThisAction = false,
  ): number {
    let actionTime = 0;
    if (action.nodeType === AutomationNodeTypes.INIT) {
      return actionTime;
    }
    if (includeThisAction && action.nodeType === AutomationNodeTypes.WAIT) {
      actionTime = action.payload.delayInSeconds;
    }
    const { parentId } = action;
    if (parentId) {
      const parentAction = this.props.nodes[parentId];
      if (parentAction) {
        actionTime += this.getActionWaitTimeRecursive(parentAction, true);
      }
    }

    return actionTime;
  }

  getWaitingTimes(includeThisAction = false): string {
    const { node } = this.props;
    const { payload } = this.state;
    const waitingTime = this.getActionWaitTimeRecursive(node, false);
    return durationConverter.toString(
      waitingTime + (includeThisAction ? payload.delayInSeconds : 0),
    );
  }

  render() {
    const preWaitingTime = this.getWaitingTimes();
    const { payload } = this.state;
    const { node, nodeLabel } = this.props;

    const isChanged = payload.delayInSeconds !== node.payload?.delayInSeconds;

    return (
      <Box width="600px">
        <ModalHead
          title={nodeLabel}
          onClose={this.closeModal}
          className={styles.modalHead}
        >
          <p>
            Specify the wait time before re-checking the initial conditions and
            executing the next actions.
          </p>
        </ModalHead>
        <ModalBody>
          <Box width="540px">
            <FlexBox>
              <FieldDuration
                includeDays
                input={{
                  name: 'delayInSeconds',
                  onChange: (value) => {
                    this.setState({
                      payload: {
                        ...payload,
                        delayInSeconds: value, // Min. 1 minutes
                      },
                    });
                  },
                  value: payload.delayInSeconds,
                }}
              />
            </FlexBox>

            {!!preWaitingTime && (
              <Box
                background="blue.100"
                borderRadius
                margin="300 0 0 0"
                padding="300"
                color="neutral.900"
              >
                You&apos;re about to add more wait time to the flow, which will
                be added to the previous{' '}
                <strong>{this.getWaitingTimes()}</strong>.
                <br />
                <br />
                The total wait time after this action will be{' '}
                <strong>{this.getWaitingTimes(true)}</strong>.
              </Box>
            )}
          </Box>
        </ModalBody>
        <ModalFoot
          confirmOnClick={this.saveHandler}
          confirmName={getConfirmButtonVerb(node)}
          confirmProps={{ disabled: !isChanged }}
          cancelOnClick={this.closeModal}
        />
      </Box>
    );
  }
}
